import { LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';
import { useCallback, useEffect, useState } from 'react';
import { getCustomerLedger } from 'app/api/customer';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const dataPerPage = 10;

const useCustomerLedger = (latestDeletionDate, latestRestorationDate) => {
  const customerData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CUSTOMER_DATA)
  );

  const [pageLoading, setPageLoading] = useState(false);

  const [flatData, setFlatData] = useState([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPageCount, setFlatPageCount] = useState(1);

  const [beforeData, setBeforeData] = useState([]);
  const [beforeTotal, setBeforeTotal] = useState(0);
  const [beforePageCount, setBeforePageCount] = useState(1);

  const [afterData, setAfterData] = useState([]);
  const [afterTotal, setAfterTotal] = useState(0);
  const [afterPageCount, setAfterPageCount] = useState(1);

  const fetchSection = useCallback(
    async (page, bucket) => {
      setPageLoading(true);
      try {
        const response = await getCustomerLedger(
          customerData?.id,
          page,
          dataPerPage,
          bucket,
          latestDeletionDate,
          latestRestorationDate
        );
        if (response?.responseCode === 200) {
          const rows = response?.data?.data || [];
          const count = response?.data?.count || 0;
          const pageCount = Math.ceil(count / dataPerPage) || 1;
          if (bucket === 'before') {
            setBeforeData(rows);
            setBeforeTotal(count);
            setBeforePageCount(pageCount);
          } else if (bucket === 'after') {
            setAfterData(rows);
            setAfterTotal(count);
            setAfterPageCount(pageCount);
          } else {
            setFlatData(rows);
            setFlatTotal(count);
            setFlatPageCount(pageCount);
          }
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [customerData?.id, latestDeletionDate, latestRestorationDate]
  );

  const fetchFlat = useCallback((page) => fetchSection(page, null), [fetchSection]);
  const fetchBefore = useCallback((page) => fetchSection(page, 'before'), [fetchSection]);
  const fetchAfter = useCallback((page) => fetchSection(page, 'after'), [fetchSection]);

  useEffect(() => {
    if (!latestDeletionDate) {
      fetchFlat(1);
      return;
    }
    fetchBefore(1);
    if (latestRestorationDate) {
      fetchAfter(1);
    }
  }, [latestDeletionDate, latestRestorationDate, fetchFlat, fetchBefore, fetchAfter]);

  return {
    pageLoading,

    flatData,
    flatTotal,
    flatPageCount,
    fetchFlat,

    beforeData,
    beforeTotal,
    beforePageCount,
    fetchBefore,

    afterData,
    afterTotal,
    afterPageCount,
    fetchAfter,

    dataPerPage,
  };
};

export default useCustomerLedger;
